import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { metaPixel } from "../../../analytics/metaPixel";
import { api, getToken, loginUrl, rupees } from "../eventShared";
import "./styles.css"; // kept: other pages may rely on these global rules

const APP_STORE_URL = "https://apps.apple.com/in/app/ota-life/id6743555476";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.otanative.ota";
const POLL_MS = 2500;
const POLL_LIMIT_MS = 60000;

// Order result after checkout. Shows what the server says about the order
// (the Razorpay webhook marks it PAID), never a "confirmed" it can't back up.
const OrderConfirmed = () => {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("order");
  const [state, setState] = useState({ status: orderId ? "loading" : "none" });

  useEffect(() => {
    if (!orderId || !getToken()) return;
    let stopped = false;
    let timer;
    const started = Date.now();

    const check = async () => {
      try {
        const res = await api(`/payment/status/${orderId}`);
        if (stopped) return;
        if (res.paymentStatus === "PENDING" && Date.now() - started < POLL_LIMIT_MS) {
          setState({ status: "waiting", order: res.order });
          timer = setTimeout(check, POLL_MS);
          return;
        }
        if (res.paymentStatus === "PAID" && res.orderType === "TICKET_ORDER" && res.order) {
          metaPixel.trackPurchase({
            orderId,
            value: res.order.finalTotalAmount,
            productId: res.order.product?.id,
            quantity: res.order.totalQuantity,
          });
        }
        setState({ status: res.paymentStatus, order: res.order });
      } catch (err) {
        if (!stopped) setState({ status: err.status === 401 ? "login" : "none" });
      }
    };
    check();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [orderId]);

  const { status, order } = state;
  const here = `/events/order-confirmed?order=${orderId}`;

  let title;
  let body;
  if (orderId && (!getToken() || status === "login")) {
    title = "Log in to see your order";
    body = (
      <Link to={loginUrl(here)} className="font-semibold text-[#3969E7]">
        Log in
      </Link>
    );
  } else if (status === "loading" || status === "waiting") {
    title = "Confirming your payment...";
    body = <p>This usually takes a few seconds. Please keep this page open.</p>;
  } else if (status === "PAID") {
    title = "You're in!";
    body = (
      <>
        <p data-testid="order-summary">
          {order?.product?.title}: {order?.totalQuantity} {order?.totalQuantity === 1 ? "pass" : "passes"},{" "}
          {Number(order?.finalTotalAmount) > 0 ? rupees(order.finalTotalAmount) : "free"}.
        </p>
        <p className="mt-3">
          Open the Ōta LIFE app with the same account (Google, Apple or email) to see your QR passes. They work offline at the gate.
        </p>
        <AppButtons />
      </>
    );
  } else if (status === "PENDING") {
    title = "Payment is still being confirmed";
    body = (
      <>
        <p>If money was taken, your passes will show up in the Ōta LIFE app within a few minutes.</p>
        <AppButtons />
      </>
    );
  } else if (status === "FAILED") {
    title = "Payment not completed";
    body = (
      <>
        <p>No passes were issued for this order.</p>
        {order?.product?.id && (
          <Link to={`/events/checkout?event=${order.product.id}`} className="mt-3 inline-block font-semibold text-[#3969E7]">
            Try again
          </Link>
        )}
      </>
    );
  } else {
    title = "Order not found";
    body = (
      <p>
        If you paid, your passes are in the Ōta LIFE app.{" "}
        <Link to="/" className="font-semibold text-[#3969E7]">
          Home
        </Link>
      </p>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-[#F5F3FF] to-white px-5">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 text-center text-sm text-gray-700 shadow" data-testid="order-confirmed">
        <h1 className="mb-3 text-2xl font-extrabold text-gray-900" data-testid="order-title">{title}</h1>
        {body}
      </div>
    </div>
  );
};

const AppButtons = () => (
  <div className="mt-4 flex gap-3">
    <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="flex-1 rounded-xl bg-black px-4 py-2 font-semibold text-white">
      App Store
    </a>
    <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" className="flex-1 rounded-xl bg-black px-4 py-2 font-semibold text-white">
      Google Play
    </a>
  </div>
);

export default OrderConfirmed;
